/**
 * The arithmetic behind the committee's charts.
 *
 * The web console and the phone draw the same five figures, each in its own
 * kit. What a chart shows is decided here, once, so the two can differ in how
 * a bar looks and never in how tall it is.
 *
 * Nothing here invents a month. A figure is drawn only from months the caller
 * actually holds, and a month nobody spent anything in is a month that spent
 * nothing, not a gap.
 */

import { type Birr, assertBirr } from "./money.js";
import type { MonthTotals } from "./overview.js";
import { type Period, addPeriods } from "./period.js";

export interface SpendRow {
  category: string;
  amount: Birr;
  period: Period;
  voided: boolean;
}

export interface NetMonth {
  period: Period;
  collected: Birr;
  spent: Birr;
  /** Collected less spent. Negative is a month at a loss. */
  net: Birr;
}

/** Verified fees against expenses for every month of the trend, oldest first. */
export function netByMonth(
  trend: readonly MonthTotals[],
  expenses: readonly SpendRow[],
): NetMonth[] {
  const spent = new Map<Period, Birr>();
  for (const expense of expenses) {
    if (expense.voided) continue;
    assertBirr(expense.amount, "expense amount");
    spent.set(expense.period, (spent.get(expense.period) ?? 0) + expense.amount);
  }
  return trend.map((month) => {
    const out = spent.get(month.period) ?? 0;
    return {
      period: month.period,
      collected: month.collected,
      spent: out,
      net: month.collected - out,
    };
  });
}

export interface CategoryShare {
  category: string;
  amount: Birr;
  /** Share of the month's spending, 0 to 1. */
  share: number;
  /** The same category the month before, so a change can be read off. */
  before: Birr;
}

/**
 * The month's spending by category, largest first, with the month before.
 *
 * A category that was paid last month and not this one is left out: the chart
 * is of this month, and a zero-width slice is not something anybody can read.
 */
export function categoryShares(expenses: readonly SpendRow[], period: Period): CategoryShare[] {
  const previous = addPeriods(period, -1);
  const now = new Map<string, Birr>();
  const then = new Map<string, Birr>();
  for (const expense of expenses) {
    if (expense.voided) continue;
    assertBirr(expense.amount, "expense amount");
    const into = expense.period === period ? now : expense.period === previous ? then : null;
    into?.set(expense.category, (into.get(expense.category) ?? 0) + expense.amount);
  }
  const total = [...now.values()].reduce((sum, amount) => sum + amount, 0);
  return [...now.entries()]
    .filter(([, amount]) => amount > 0)
    .map(([category, amount]) => ({
      category,
      amount,
      share: total > 0 ? amount / total : 0,
      before: then.get(category) ?? 0,
    }))
    .sort((a, b) => b.amount - a.amount || a.category.localeCompare(b.category));
}

/**
 * The largest categories by name and the rest as one "other" row.
 *
 * Six bars is what a phone can label without rotating text, so a month spread
 * over nine categories keeps its biggest five and folds the tail. A category
 * already called "other" joins the fold rather than appearing twice.
 */
export function foldSmall(shares: readonly CategoryShare[], keep = 5): CategoryShare[] {
  const named = shares.filter((share) => share.category !== "other");
  const loose = shares.filter((share) => share.category === "other");
  if (named.length <= keep && loose.length === 0) return [...shares];
  const head = named.slice(0, named.length <= keep ? named.length : keep);
  const tail = [...named.slice(head.length), ...loose];
  if (tail.length === 0) return head;
  const other = tail.reduce(
    (sum, share) => ({
      category: "other",
      amount: sum.amount + share.amount,
      share: sum.share + share.share,
      before: sum.before + share.before,
    }),
    { category: "other", amount: 0, share: 0, before: 0 },
  );
  return [...head, other];
}

/**
 * The average verified per finished month, as a line to read the columns
 * against. The month still open is left out, because half a month pulls the
 * average down and makes every finished month look better than it was.
 */
export function finishedAverage(trend: readonly MonthTotals[], open: Period): Birr | null {
  const done = trend.filter((month) => month.period !== open && month.billed > 0);
  if (done.length < 2) return null;
  return Math.round(done.reduce((sum, month) => sum + month.collected, 0) / done.length);
}

export type StepKind = "in" | "out" | "net";

export interface Step {
  /** A category for an expense step, empty for the first and last bars. */
  category: string;
  kind: StepKind;
  /** Where the bar starts and ends on the value axis. `from` is the lower end. */
  from: Birr;
  to: Birr;
  amount: Birr;
}

/**
 * A waterfall from what came in, down through each expense, to what is left.
 *
 * When spending passes income the running total goes below zero, and the last
 * bar hangs beneath the axis rather than being clamped to it, because a month
 * at a loss drawn as a month at zero is a month somebody has hidden.
 */
export function waterfall(collected: Birr, shares: readonly CategoryShare[]): Step[] {
  assertBirr(collected, "collected");
  const steps: Step[] = [{ category: "", kind: "in", from: 0, to: collected, amount: collected }];
  let running = collected;
  for (const share of shares) {
    const next = running - share.amount;
    steps.push({
      category: share.category,
      kind: "out",
      from: Math.min(next, running),
      to: Math.max(next, running),
      amount: share.amount,
    });
    running = next;
  }
  steps.push({
    category: "",
    kind: "net",
    from: Math.min(0, running),
    to: Math.max(0, running),
    amount: running,
  });
  return steps;
}
